import { actionCreator, asyncActionCreators } from "../../lib/util/ReduxUtils";
import * as Api from "../../Api";
import { DataPage } from "../../Api";

import * as FleetSelectors from "../../store/Fleet/Fleet.selector";
import * as Flags from "../../lib/Flags";
import { DEMO_FLEET_ID } from "../../lib/Flags";
import { Insight } from "../../lib/types/InsightTypes";
import { User } from "../../lib/types/UserTypes";
import { Fleet } from "../../lib/types/Fleet";
import { Group } from "../../lib/types/Group";
import { ScopeInfo } from "../../lib/types/MiscTypes";
import { SupportResolvedFilter } from "./sub-components/SupportPanel";
import { Journey } from "../../lib/types/JourneyTypes";
import { LogEntry } from "./sub-components/SupportPageStatus/SupportPageStatus";
import * as GlobalActions from "../../store/Global/Global.action";

export const SUPPORT_USER_LIST_SCOPES = ["support", "auth"];
export const SUPPORT_USER_SCOPES = [
	...SUPPORT_USER_LIST_SCOPES,
	"account",
	"multiFactor",
];

// get data
export const getFleetVehicles = asyncActionCreators<SupportResolvedFilter, any>(
	"[SUPPORT] GET_FLEET_VEHICLES",
	async (action) => {
		const { fleetId } = action.payload;

		const res = await Api.fetchFleetVehicles.submit({
			params: { fleetId: fleetId },
			query: {},
			options: {
				lock: "fleet_vehicles",
			},
		});
		return res.data;
	},
);
export const getFleetJourneys = asyncActionCreators<
	SupportResolvedFilter & {
		pagination: boolean;
		pageSize: number;
		pageIndex: number;
		pageToken: string | undefined;
	},
	Journey[]
>("[SUPPORT] GET_FLEET_JOURNEYS", async (action) => {
	const {
		fleetId,
		from,
		to,
		userId,
		group,
		pagination,
		pageSize,
		pageIndex,
		pageToken,
	} = action.payload;

	if (pagination) {
		const res = await Api.fetchFleetJourneysPage.submit({
			params: {
				fleetId: fleetId,
			},
			query: {
				userId: userId,
				group: (!userId && group) || undefined,
				pageIndex: pageIndex,
				pageSize: pageSize,
				pageToken: pageToken,
			},
			options: { cache: false },
		});
		//this.setState({ cursorToken: res.data.cursorToken });
		return res.data.results;
	}

	const res = await Api.fetchFleetJourneys.submit({
		params: { fleetId: fleetId },
		options: {
			cache: false,
			lock: "fleet_journey_list",
		},
		query: {
			from: from || undefined,
			to: to || undefined,
			userId: userId || undefined,
			group: group || undefined,
			includeManual: true,
		},
	});
	return res.data.reverse();
});

export const getFleetUsers = asyncActionCreators<
	ScopeInfo & { includeArchived?: boolean },
	User[]
>("[SUPPORT] GET_FLEET_USERS", async (action) => {
	const { fleetId, group, includeArchived } = action.payload;

	if (fleetId === FleetSelectors.GLOBAL_FLEET_ID) {
		throw new Error("Can't get users for global fleet");
	}

	const result = await Api.fetchFleetUsers.submit({
		params: { fleetId: fleetId },
		options: {
			cache: false,
			lock: "fleet_users_list",
			maxRetries: 0,
		},
		query: {
			scopes: SUPPORT_USER_LIST_SCOPES,
			includeInvites: true,
			includeManagers: true,
			group: group || undefined,
			includeArchived: Boolean(includeArchived),
		},
	});
	return result.data;
});

export const getFleetInsights = asyncActionCreators<
	SupportResolvedFilter,
	Insight[]
>("[SUPPORT] GET_FLEET_INSIGHTS", async (action) => {
	const { fleetId, group, userId, from, to, loggedInUserId } = action.payload;

	if (!userId && !loggedInUserId) {
		throw new Error("No user");
	}

	const result = await Api.fetchInsights.submit({
		options: {
			cache: false,
			lock: "fleet_insights",
		},
		query: {
			fleetId: fleetId === FleetSelectors.GLOBAL_FLEET_ID ? undefined : fleetId,
			group: group || undefined,
			includeTemp: true,
			from: from,
			to: to,
		},
	});
	return result.data;
});

export const getFleets = asyncActionCreators<void, Fleet[]>(
	"[SUPPORT] GET_FLEETS",
	async () => {
		const result = await Api.fetchFleets.submit({
			query: {
				detailed: true,
				includeUserSummary: true,
				showArchived: Flags.SHOW_ARCHIVED_FLEETS.is(),
			},
			options: {
				cache: false,
				lock: "support_fleets",
			},
		});
		return result.data;
	},
);

export const getLogs = asyncActionCreators<
	{
		filter: SupportResolvedFilter;
		pageIndex: number;
		pageSize: number;
		pageToken: string;
	},
	DataPage<LogEntry> & { index: number }
>("[SUPPORT] GET_LOGS", async (action, dispatch, getState) => {
	const { fleetId, ...query } = action.payload.filter;
	const { pageToken, pageIndex, pageSize } = action.payload;

	const result = await Api.fetchFleetLogsPage.submit({
		params: { fleetId: fleetId },
		query: { ...query, pageToken, pageSize },
		options: {
			cache: false,
			lock: "support_logs",
		},
	});
	return Object.assign(result.data, { index: pageIndex });
});

export const updateFleet = actionCreator("[SUPPORT] UPDATE_FLEET");

export const getFleetGroups = asyncActionCreators<
	SupportResolvedFilter,
	Group[]
>("[SUPPORT] GET_FLEET_GROUPS", async (action, dispatch, getState) => {
	const { fleetId } = action.payload;

	if (fleetId === DEMO_FLEET_ID || fleetId === FleetSelectors.GLOBAL_FLEET_ID) {
		return [];
	}

	const result = await Api.fetchFleet.submit({
		params: { fleetId: fleetId },
		options: {
			lock: "support_fleet_groups",
		},
	});
	return result.data.groups;
});

export const getComps = asyncActionCreators<SupportResolvedFilter, any>(
	"[SUPPORT] GET_COMPS",
	async (action) => {
		const { fleetId, group, ...query } = action.payload;
		const res = await Api.fetchCompetitions.submit({
			options: {
				cache: false,
				lock: "comps",
			},
			query: {
				...query,
				group: group || undefined,
				fleetId:
					fleetId === FleetSelectors.GLOBAL_FLEET_ID ? undefined : fleetId,
				includeGlobal: true,
			},
		});

		const comps = res.data.comps?.map((currentComp: any) => {
			if (
				currentComp.winners?.length > 1 &&
				currentComp.numberOfWinners === undefined &&
				!currentComp.runnersUp
			) {
				// In old competitions runners up are in the winners object
				// We need to get them set them as runners up here
				currentComp.runnersUp = currentComp.winners.slice(1);
				currentComp.winners = currentComp.winners.slice(0, 1);
			}

			return currentComp;
		});

		return { results: comps, prizes: res.data.prizes };
	},
);
export const getCompEntries = asyncActionCreators<string, any>(
	"[SUPPORT] GET_COMP_ENTRIES",
	async (action, dispatch, getState) => {
		const compId = action.payload;

		const res = await Api.fetchCompetitionEntries.submit({
			params: { competitionId: compId },
			options: {
				lock: "comp_entries",
			},
		});
		return res.data;
	},
);

export const updateJourney = asyncActionCreators<
	{ journeyId: string; data: any },
	any
>("[SUPPORT] UPDATE_JOURNEY", async (action, dispatch, getState) => {
	const { journeyId, data } = action.payload;
	const { mode, type, transportType, hidden, unscorable } = data;

	const res = await Api.postJourneyUpdate.submit({
		params: { journeyId: journeyId },
		body: {
			unscorable: unscorable,
			transportType: transportType,
			mode: mode || undefined,
			type: type || undefined,
			hidden: hidden,
		},
	});

	// Change journey in main view
	try {
		const fleetJourneys = [...getState().SupportReducer.fleetJourneys.value];
		const index = fleetJourneys.findIndex(
			(journey) => journey.key === journeyId,
		);
		if (index >= 0) {
			fleetJourneys[index] = res.data;
			dispatch(getFleetJourneys.resolved(fleetJourneys));
		}
	} catch (error) {
		console.error(error);
	}

	return res.data;
});

export const addOrUpdateInsight = actionCreator<any>(
	"[SUPPORT] ADD_OR_UPDATE_INSIGHT",
	(action, dispatch, getState) => {
		const { id, create, insightData } = action.payload;
		const insights: Insight[] | undefined =
			getState().SupportReducer.fleetInsights.value;

		if (insights) {
			if (create) {
				insights.unshift(insightData);
			} else {
				insights.some((insight, index) => {
					if (insight.id === id) {
						insights.splice(index, 1, {
							...insight,
							...insightData,
						});
					}

					return insight.id === id;
				});
			}
		}

		action.payload = insights;
	},
);
export const deleteInsight = actionCreator("[SUPPORT] DELETE_INSIGHT");

export const updateInsights = actionCreator<any>(
	"[SUPPORT] UPDATE_INSIGHTS",
	(action, dispatch, getState) => {
		const updatedInsights: { id: string; data: Insight }[] = action.payload;
		const insights: Insight[] | undefined =
			getState().SupportReducer.fleetInsights.value;

		action.payload = insights?.map((insight, index) => {
			const update = updatedInsights.find((i) => i.id === insight.id);
			return update ? Object.assign(insight, update.data) : insight;
		});
	},
);

export const deleteInsights = actionCreator("[SUPPORT] DELETE_INSIGHTS");

export const changeJourney = actionCreator("[SUPPORT] CHANGE_JOURNEY");

export const changeFleetUser = actionCreator<{
	user: User;
	data: Partial<User>;
	noApiUpdate?: boolean;
}>("[SUPPORT] CHANGE_FLEET_USER", (action, dispatch, getState) => {
	const { user, data } = action.payload;

	dispatch(GlobalActions.updateDriver(action.payload));

	const users: { value?: User[]; resolved: boolean } =
		getState().SupportReducer.fleetUsers;
	if (users.resolved) {
		if (users.value?.some((candidate) => candidate.uid === user.uid)) {
			const newUsers: User[] = users.value.map((candidate) =>
				candidate.uid === user.uid ? { ...candidate, ...data } : candidate,
			);
			dispatch(getFleetUsers.resolved(newUsers));
		}
	}
	return action;
});
